"use client";

import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from "react";

type SidebarContextType = {
  isCollapsed: boolean;
  isSidebarOpen: boolean;
  /**
   * True once the persisted collapse preference has been read and applied.
   * Consumers use this to suppress the collapse/expand width transition for
   * the very first render, so restoring a saved "collapsed" state doesn't
   * play a spurious expand-then-collapse animation on load.
   */
  isCollapsePreferenceReady: boolean;
  toggleCollapsed: () => void;
  toggleSidebarOpen: () => void;
  setIsSidebarOpen: (open: boolean) => void;
};

const STORAGE_KEY = "sidebar_collapsed";

// useLayoutEffect runs synchronously before the browser paints, so the
// restored preference is applied before anything is shown on screen. On the
// server it doesn't exist, so fall back to useEffect there (Next.js still
// renders this client component once during SSR).
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

function readStoredCollapsed(): boolean | null {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === null ? null : saved === "true";
  } catch {
    // localStorage can throw in some privacy modes — fall back to the default.
    return null;
  }
}

function writeStoredCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Ignore write failures (e.g. storage disabled/full).
  }
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsePreferenceReady, setIsCollapsePreferenceReady] = useState(false);

  // Restore the persisted preference before the browser's first paint, so
  // there is no visible flash of the default (expanded) state on refresh.
  useIsomorphicLayoutEffect(() => {
    const saved = readStoredCollapsed();
    if (saved !== null) {
      setIsCollapsed(saved);
    }
    setIsCollapsePreferenceReady(true);
  }, []);

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      writeStoredCollapsed(next);
      return next;
    });
  };

  const toggleSidebarOpen = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        isSidebarOpen,
        isCollapsePreferenceReady,
        toggleCollapsed,
        toggleSidebarOpen,
        setIsSidebarOpen,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
